import { get, set, del, keys } from "idb-keyval";
import { QUEUE } from "./config";
import { debugWarn } from "./debug";
import { isBrowser } from "./env";
import { metrics } from "./metrics";
import type { EnrichedEvent } from "./types";

// In-memory pre-consent buffer (drained on consent decision).
let preConsentBuffer: EnrichedEvent[] = [];

// Rate limit (per-minute, rolling).
const rateLog: number[] = [];
function withinRateLimit(): boolean {
  const now = Date.now();
  while (rateLog.length && now - rateLog[0] > 60_000) rateLog.shift();
  if (rateLog.length >= QUEUE.rate_limit_per_minute) return false;
  rateLog.push(now);
  return true;
}

export function bufferPreConsent(evt: EnrichedEvent): void {
  if (preConsentBuffer.length >= QUEUE.pre_consent_max) {
    // Drop LOW priority first, never blindly FIFO.
    const lowIdx = preConsentBuffer.findIndex((e) => e.priority === "low");
    if (lowIdx >= 0) preConsentBuffer.splice(lowIdx, 1);
    else preConsentBuffer.shift(); // all high — fall back to FIFO
    metrics.inc("dropped_pre_consent");
    metrics.inc("dropped_events_total");
  }
  preConsentBuffer.push(evt);
  metrics.inc("events_buffered_pre_consent");
}

export function drainPreConsent(): EnrichedEvent[] {
  const drained = preConsentBuffer;
  preConsentBuffer = [];
  // Filter out non-replayable events on drain.
  return drained.filter((e) => e.consentReplayable);
}

export function checkRateLimit(): boolean {
  const ok = withinRateLimit();
  if (!ok) {
    metrics.inc("dropped_rate_limit");
    metrics.inc("dropped_events_total");
  }
  return ok;
}

// ---- Offline queue (IndexedDB via idb-keyval) ----

interface OfflineRecord {
  k: string; // key
  e: EnrichedEvent;
  ts: number; // enqueue time
}

const NS = QUEUE.offline_namespace;

function recordKey(): string {
  return `${NS}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 8)}`;
}

export async function offlineEnqueue(evt: EnrichedEvent): Promise<void> {
  if (!isBrowser()) return;
  try {
    const all = await offlineList();
    if (all.length >= QUEUE.offline_max) {
      // Drop low-priority first, never high.
      const low = all.find((r) => r.e.priority === "low");
      if (low) await del(low.k);
      else await del(all[0].k); // all high → drop oldest
      metrics.inc("dropped_events_total");
    }
    const k = recordKey();
    const rec: OfflineRecord = { k, e: evt, ts: Date.now() };
    await set(k, rec);
    metrics.setOfflineSize((await offlineList()).length);
  } catch (err) {
    debugWarn("offlineEnqueue failed", err);
  }
}

export async function offlineList(): Promise<OfflineRecord[]> {
  if (!isBrowser()) return [];
  try {
    const ks = (await keys()) as string[];
    const matching = ks.filter((k) => typeof k === "string" && k.startsWith(`${NS}:`));
    const recs = await Promise.all(matching.map((k) => get<OfflineRecord>(k)));
    const now = Date.now();
    const valid: OfflineRecord[] = [];
    for (const r of recs) {
      if (!r) continue;
      if (now - r.ts > QUEUE.offline_ttl_ms) {
        await del(r.k).catch(() => undefined);
        continue;
      }
      valid.push(r);
    }
    return valid.sort((a, b) => (b.e.priority === "high" ? 1 : 0) - (a.e.priority === "high" ? 1 : 0));
  } catch {
    return [];
  }
}

export async function offlineDelete(key: string): Promise<void> {
  if (!isBrowser()) return;
  try {
    await del(key);
    metrics.setOfflineSize((await offlineList()).length);
  } catch {
    /* noop */
  }
}

